import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Server, ArrowLeft, ShieldCheck, FileText, Building2, Mail, Phone, MessageCircle, Globe, MapPin } from "lucide-react";
import { useBranding } from "../../hooks/useBranding";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const applyPlaceholders = (text, namaApp, p) => {
  if (!text) return "";
  const map = {
    "{{nama}}": namaApp,
    "{{badan_usaha}}": p.badan_usaha || namaApp,
    "{{alamat}}": p.alamat || "-",
    "{{telepon}}": p.telepon || "-",
    "{{whatsapp}}": p.whatsapp || "-",
    "{{email}}": p.email || "-",
    "{{website}}": p.website || "-",
  };
  return Object.keys(map).reduce((acc, k) => acc.split(k).join(map[k] ?? ""), text);
};

const renderInline = (line, key) => {
  // Support **bold**
  const parts = line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return parts.map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={`${key}-${i}`} className="text-white">{part.slice(2, -2)}</strong>
      : <span key={`${key}-${i}`}>{part}</span>
  );
};

const MarkdownContent = ({ text }) => {
  const lines = (text || "").split("\n");
  const blocks = [];
  let list = [];
  const flushList = (idx) => {
    if (list.length) {
      blocks.push(<ul key={`ul-${idx}`} className="list-disc list-inside space-y-1">{list.map((li, i) => <li key={i}>{renderInline(li, `li-${idx}-${i}`)}</li>)}</ul>);
      list = [];
    }
  };
  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (line.startsWith("- ")) { list.push(line.slice(2)); return; }
    flushList(idx);
    if (line.startsWith("## ")) blocks.push(<h2 key={idx} className="text-base font-bold text-white pt-3">{renderInline(line.slice(3), `h-${idx}`)}</h2>);
    else if (line.trim() === "") { /* spacer */ }
    else blocks.push(<p key={idx}>{renderInline(line, `p-${idx}`)}</p>);
  });
  flushList("end");
  return <>{blocks}</>;
};

const LegalLayout = ({ icon: Icon, title, subtitle, children, testid }) => {
  const branding = useBranding();
  const p = branding.perusahaan || {};
  const namaApp = branding.app_name || p.nama_isp || "NUSA NET FIBER";
  const year = new Date().getFullYear();
  const navLinks = [
    { to: "/tentang-kami", label: "Tentang Kami" },
    { to: "/kebijakan-privasi", label: "Kebijakan Privasi" },
    { to: "/syarat-ketentuan", label: "Syarat & Ketentuan" },
    { to: "/kontak", label: "Kontak" },
  ];

  return (
    <div className="min-h-screen bg-[#060a14] text-slate-200" data-testid={testid}>
      <header className="border-b border-slate-800 bg-[#0a1120]/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="legal-brand-home">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-tr from-cyan-500 via-blue-600 to-indigo-600 border border-cyan-400/30 overflow-hidden">
              {branding.app_logo ? <img src={branding.app_logo} alt="Logo" className="w-full h-full object-contain" /> : <Server className="w-5 h-5 text-white" />}
            </span>
            <span className="font-black text-white text-sm">{namaApp}</span>
          </Link>
          <Link to="/login" className="text-xs text-cyan-400 hover:underline flex items-center gap-1" data-testid="legal-back-login">
            <ArrowLeft className="w-3.5 h-3.5" /> Masuk
          </Link>
        </div>
      </header>

      <nav className="max-w-4xl mx-auto px-5 pt-6 flex flex-wrap gap-2">
        {navLinks.map((l) => (
          <Link key={l.to} to={l.to} className="px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-xs text-slate-300 hover:border-cyan-500/50 hover:text-cyan-300 transition-colors">
            {l.label}
          </Link>
        ))}
      </nav>

      <main className="max-w-4xl mx-auto px-5 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-2xl bg-cyan-500/15 text-cyan-400 flex items-center justify-center">
            <Icon className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-white">{title}</h1>
            {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
          </div>
        </div>

        <article className="space-y-3 text-sm leading-relaxed text-slate-300">
          {children}
        </article>
      </main>

      <footer className="border-t border-slate-800 mt-8">
        <div className="max-w-4xl mx-auto px-5 py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-slate-500">
          <span>© {year} {p.badan_usaha || namaApp}. Seluruh hak cipta dilindungi.</span>
          <div className="flex flex-wrap gap-3">
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} className="hover:text-cyan-300">{l.label}</Link>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

// Halaman konten dinamis (isi diambil dari backend & bisa diedit admin)
const LegalDocPage = ({ slug, icon, subtitle, testid }) => {
  const branding = useBranding();
  const p = branding.perusahaan || {};
  const namaApp = branding.app_name || p.nama_isp || "NUSA NET FIBER";
  const [data, setData] = useState({ title: "", content: "", updated_at: "" });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/legal/${slug}`)
      .then((res) => setData(res.data))
      .catch(() => setData({ title: "", content: "Konten belum tersedia.", updated_at: "" }));
  }, [slug]);

  const content = applyPlaceholders(data.content, namaApp, p);

  return (
    <LegalLayout icon={icon} title={data.title || subtitle} subtitle={subtitle} testid={testid}>
      <MarkdownContent text={content} />
      {data.updated_at && (
        <p className="text-[11px] text-slate-500 pt-6">Terakhir diperbarui: {new Date(data.updated_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}</p>
      )}
    </LegalLayout>
  );
};

export const TentangKamiPage = () => (
  <LegalDocPage slug="tentang-kami" icon={Building2} subtitle="Mengenal lebih dekat layanan kami" testid="tentang-kami-page" />
);

export const KebijakanPrivasiPage = () => (
  <LegalDocPage slug="kebijakan-privasi" icon={ShieldCheck} subtitle="Bagaimana kami mengumpulkan, menggunakan, dan melindungi data Anda" testid="kebijakan-privasi-page" />
);

export const SyaratKetentuanPage = () => (
  <LegalDocPage slug="syarat-ketentuan" icon={FileText} subtitle="Ketentuan penggunaan layanan dan aplikasi" testid="syarat-ketentuan-page" />
);

export const KontakPage = () => {
  const branding = useBranding();
  const p = branding.perusahaan || {};
  const namaApp = branding.app_name || p.nama_isp || "NUSA NET FIBER";
  const waLink = p.whatsapp ? `https://wa.me/${String(p.whatsapp).replace(/[^0-9]/g, "")}` : null;
  return (
    <LegalLayout icon={MessageCircle} title="Kontak Kami" subtitle={`Hubungi tim ${namaApp}`} testid="kontak-page">
      <p>Kami siap membantu Anda. Silakan hubungi kami melalui saluran berikut:</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {p.alamat && (
          <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 flex items-start gap-3">
            <MapPin className="w-5 h-5 text-cyan-400 shrink-0" />
            <div><p className="text-white text-sm font-semibold">Alamat</p><p className="text-xs text-slate-400">{p.alamat}</p></div>
          </div>
        )}
        {p.telepon && (
          <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 flex items-start gap-3">
            <Phone className="w-5 h-5 text-cyan-400 shrink-0" />
            <div><p className="text-white text-sm font-semibold">Telepon</p><p className="text-xs text-slate-400">{p.telepon}</p></div>
          </div>
        )}
        {p.whatsapp && (
          <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 flex items-start gap-3">
            <MessageCircle className="w-5 h-5 text-emerald-400 shrink-0" />
            <div><p className="text-white text-sm font-semibold">WhatsApp</p>{waLink ? <a href={waLink} className="text-xs text-emerald-400 hover:underline" target="_blank" rel="noopener noreferrer">{p.whatsapp}</a> : <p className="text-xs text-slate-400">{p.whatsapp}</p>}</div>
          </div>
        )}
        {p.email && (
          <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 flex items-start gap-3">
            <Mail className="w-5 h-5 text-cyan-400 shrink-0" />
            <div><p className="text-white text-sm font-semibold">Email</p><a href={`mailto:${p.email}`} className="text-xs text-cyan-400 hover:underline">{p.email}</a></div>
          </div>
        )}
        {p.website && (
          <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 flex items-start gap-3">
            <Globe className="w-5 h-5 text-cyan-400 shrink-0" />
            <div><p className="text-white text-sm font-semibold">Website</p><a href={p.website} className="text-xs text-cyan-400 hover:underline" target="_blank" rel="noopener noreferrer">{p.website}</a></div>
          </div>
        )}
      </div>
    </LegalLayout>
  );
};
